import * as React from "react";

interface UseScrollSpyOptions {
  /** IDs of sections to observe */
  sectionIds: string[];
  /** The scrollable container element */
  container: HTMLElement | null;
  /** Root margin for intersection observer - controls when section is considered "active" */
  rootMargin?: string;
}

/**
 * Hook that tracks which section is currently visible in a scrollable container.
 * Uses Intersection Observer for performance.
 */
export function useScrollSpy({
  sectionIds,
  container,
  rootMargin = "-10% 0px -80% 0px",
}: UseScrollSpyOptions): string | null {
  const [activeId, setActiveId] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!container || sectionIds.length === 0) return;

    // Track all intersecting entries to find the topmost visible one
    const intersectingEntries = new Map<string, IntersectionObserverEntry>();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            intersectingEntries.set(entry.target.id, entry);
          } else {
            intersectingEntries.delete(entry.target.id);
          }
        });

        // Find the topmost visible section (by DOM order in sectionIds)
        for (const id of sectionIds) {
          if (intersectingEntries.has(id)) {
            setActiveId(id);
            return;
          }
        }

        // If no sections are intersecting, keep the current active
      },
      {
        root: container,
        rootMargin,
        threshold: 0,
      },
    );

    // Observe all sections
    sectionIds.forEach((id) => {
      const element = document.getElementById(id);
      if (element) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, [sectionIds, container, rootMargin]);

  return activeId;
}
